import * as THREE from 'three';
import { AssetManager } from './Assets';
import { EntityManager } from './Entities';
import { UIState } from '../ui/store';
import { WedgeState } from '../types';
import { createWedgeMesh } from './tools/WedgeTool';

const GUIDEBAR_SIZE = new THREE.Vector3(0.2, 0.8, 6.5);
const GUIDEBAR_OFFSET = new THREE.Vector3(0.75, 0, -5);
const GUIDEBAR_ROTATION = new THREE.Euler(0.1, 0, 0);
const LOG_INTERSECTION_PADDING = 0.0;

// Reusable scratch objects to eliminate per-frame garbage collection pressure
const _scratchV1 = new THREE.Vector3();
const _scratchV2 = new THREE.Vector3();
const _scratchV3 = new THREE.Vector3();
const _scratchV4 = new THREE.Vector3();
const _scratchQ1 = new THREE.Quaternion();
const _scratchBox1 = new THREE.Box3();
const _scratchBox2 = new THREE.Box3();

export class ToolSystem {
  public chainsawMesh: THREE.Object3D | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  public throttleOn = false;

  public isCutting = false;
  public guidebarBox: THREE.Mesh | null = null;
  public kerfBoxMesh: THREE.Mesh | null = null;
  public cutStartPos: THREE.Vector3 | null = null;
  public cutStartQuat: THREE.Quaternion | null = null;
  public showKerfBoxDebug = false;

  private calculateCutSweep(): { center: THREE.Vector3; sweptScale: THREE.Vector3 } | null {
    if (!this.guidebarBox || !this.cutStartPos || !this.cutStartQuat) return null;

    this.guidebarBox.getWorldPosition(_scratchV1);
    _scratchV2.subVectors(_scratchV1, this.cutStartPos);
    _scratchQ1.copy(this.cutStartQuat).invert();
    _scratchV2.applyQuaternion(_scratchQ1);

    this.guidebarBox.getWorldScale(_scratchV3);
    _scratchV4.copy(GUIDEBAR_SIZE).multiply(_scratchV3);

    const sweptScale = new THREE.Vector3(
      _scratchV4.x,
      _scratchV4.y + Math.abs(_scratchV2.y),
      _scratchV4.z
    );

    _scratchV1.set(_scratchV2.x, _scratchV2.y / 2, _scratchV2.z);
    const center = _scratchV1.applyQuaternion(this.cutStartQuat).add(this.cutStartPos).clone();
    return { center, sweptScale };
  }

  public getGuidebarTransform() {
    if (!this.guidebarBox || this.chainsawMesh?.visible === false) return null;

    if (this.isCutting && this.cutStartPos && this.cutStartQuat) {
      const sweep = this.calculateCutSweep();
      if (sweep) {
        return { pos: sweep.center, quat: this.cutStartQuat.clone(), scale: sweep.sweptScale };
      }
    }

    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    this.guidebarBox.getWorldPosition(pos);
    this.guidebarBox.getWorldQuaternion(quat);
    this.guidebarBox.getWorldScale(_scratchV1);
    scale.copy(GUIDEBAR_SIZE).multiply(_scratchV1);
    return { pos, quat, scale };
  }

  constructor(
    private entities: EntityManager,
    private onInitiateCut?: (point: THREE.Vector3, normal: THREE.Vector3) => void,
    private onCutProgress?: (pos: THREE.Vector3, quat: THREE.Quaternion, scale: THREE.Vector3) => void
  ) {
    this.initChainsaw();
  }

  private initChainsaw() {
    const gltf = AssetManager.getModel('/assets/models/chainsaw-v1.glb');
    if (!gltf) {
      console.warn('Chainsaw model not found.');
      return;
    }

    // Clone shared scene to avoid mutating the cached AssetManager model across simulator sessions
    this.chainsawMesh = gltf.scene.clone(true);
    this.chainsawMesh.scale.set(0.2, 0.2, 0.2);

    // Setup Shadows
    this.chainsawMesh.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    // Reset initial position
    this.resetPosition();

    // Create Guidebar Bounding Box
    const boxGeo = new THREE.BoxGeometry(GUIDEBAR_SIZE.x, GUIDEBAR_SIZE.y, GUIDEBAR_SIZE.z);

    const boxMat = new THREE.MeshBasicMaterial({ color: 0xffa500, wireframe: true, depthTest: false });
    this.guidebarBox = new THREE.Mesh(boxGeo, boxMat);
    this.guidebarBox.position.copy(GUIDEBAR_OFFSET);
    this.guidebarBox.setRotationFromEuler(GUIDEBAR_ROTATION);
    this.guidebarBox.visible = false;
    this.chainsawMesh.add(this.guidebarBox);

    // Register with Entity Manager
    this.entities.add({
      id: 'tool-chainsaw',
      type: 'tool',
      mesh: this.chainsawMesh
    });

    // Create Kerf Box Bounding Mesh
    const kerfBoxGeo = new THREE.BoxGeometry(1, 1, 1);
    const kerfBoxMat = new THREE.MeshBasicMaterial({ color: 0xff00ff, wireframe: true, depthTest: false });
    this.kerfBoxMesh = new THREE.Mesh(kerfBoxGeo, kerfBoxMat);
    this.kerfBoxMesh.visible = false;
    this.entities.add({
      id: 'tool-kerfbox',
      type: 'tool',
      mesh: this.kerfBoxMesh
    });

    // Setup Animation Mixer for the chain
    if (gltf.animations && gltf.animations.length > 0) {
      this.mixer = new THREE.AnimationMixer(this.chainsawMesh);
      const action = this.mixer.clipAction(gltf.animations[0]);
      action.play();
    }
  }

  public tick(dt: number) {
    if (this.throttleOn && this.mixer) {
      this.mixer.update(dt);
    }

    if (this.kerfBoxMesh) {
      this.kerfBoxMesh.visible = this.showKerfBoxDebug;
    }

    if (this.chainsawMesh && this.guidebarBox) {
      if (this.kerfBoxMesh && !this.isCutting) {
        // Sync continuously while hovering without allocating new vectors
        this.guidebarBox.getWorldPosition(this.kerfBoxMesh.position);
        this.guidebarBox.getWorldQuaternion(this.kerfBoxMesh.quaternion);
        this.guidebarBox.getWorldScale(_scratchV1);
        this.kerfBoxMesh.scale.copy(GUIDEBAR_SIZE).multiply(_scratchV1);
      }

      const log = this.getClosestLogInfo();
      if (log) {
        this.guidebarBox.getWorldPosition(_scratchV1);
        
        _scratchBox1.setFromObject(this.guidebarBox);
        _scratchBox2.copy(log.boundingBox).expandByScalar(LOG_INTERSECTION_PADDING);
        const intersecting = _scratchBox2.intersectsBox(_scratchBox1);

        if (intersecting && !this.isCutting && this.throttleOn) {
          this.guidebarBox.getWorldQuaternion(_scratchQ1);
          const normal = _scratchV2.set(1, 0, 0).applyQuaternion(_scratchQ1).normalize();

          let longitudinalAxis = _scratchV3.set(1, 0, 0);

          if (log.entity && log.entity.segments && log.entity.segments.length >= 2 && log.entity.vertexMap) {
            const firstRing = log.entity.segments[0];
            const lastRing = log.entity.segments[log.entity.segments.length - 1];

            const posAttr = (log.entity.mesh as THREE.Mesh).geometry.getAttribute('position');
            const vIdxStart = log.entity.vertexMap[firstRing.pithIdx]?.[0];
            const vIdxEnd = log.entity.vertexMap[lastRing.pithIdx]?.[0];

            if (vIdxStart !== undefined && vIdxEnd !== undefined) {
              const startPos = _scratchV4.fromBufferAttribute(posAttr, vIdxStart);
              const endPos = new THREE.Vector3().fromBufferAttribute(posAttr, vIdxEnd);
              longitudinalAxis.subVectors(endPos, startPos).normalize();
            }
          }

          let angleDeg = normal.angleTo(longitudinalAxis) * THREE.MathUtils.RAD2DEG;
          if (angleDeg > 90) angleDeg = 180 - angleDeg;

          if (angleDeg <= 35) {
            console.log(`[ToolSystem] Valid cross-cut initiated at angle ${angleDeg.toFixed(1)}°`);
            if (this.onInitiateCut) {
              this.onInitiateCut(_scratchV1.clone(), normal.clone());
            }
            this.isCutting = true;
          } else {
            console.warn(`[ToolSystem] INVALID CUT REJECTED: Angle ${angleDeg.toFixed(1)}° exceeds 35° cross-cut limit (Rip cuts not supported).`);
            this.isCutting = true;
          }
          
          if (this.guidebarBox) {
            if (!this.cutStartPos) this.cutStartPos = new THREE.Vector3();
            if (!this.cutStartQuat) this.cutStartQuat = new THREE.Quaternion();
            this.guidebarBox.getWorldPosition(this.cutStartPos);
            this.guidebarBox.getWorldQuaternion(this.cutStartQuat);
          }
        } else if (intersecting && this.isCutting && this.throttleOn) {
          const sweep = this.calculateCutSweep();
          if (sweep && this.kerfBoxMesh && this.cutStartQuat) {
            this.kerfBoxMesh.position.copy(sweep.center);
            this.kerfBoxMesh.scale.copy(sweep.sweptScale);
            this.guidebarBox.getWorldQuaternion(this.kerfBoxMesh.quaternion);

            if (this.onCutProgress) {
              this.onCutProgress(sweep.center, this.cutStartQuat, sweep.sweptScale);
            }
          }
        } else if (!intersecting && this.isCutting) {
          const sweep = this.calculateCutSweep();
          if (sweep && this.cutStartQuat && this.onCutProgress) {
            this.onCutProgress(sweep.center, this.cutStartQuat, sweep.sweptScale);
          }
          this.isCutting = false;
        }
      }
    }
  }

  public setThrottle(on: boolean) {
    this.throttleOn = on;
    UIState.update({ throttleOn: on });
    if (on) {
      if (this.chainsawMesh && this.guidebarBox && this.kerfBoxMesh) {
         // Force world matrix update so getWorldPosition/Scale are correct and not stale
         this.chainsawMesh.updateMatrixWorld(true);
         
         this.kerfBoxMesh.position.copy(this.guidebarBox.getWorldPosition(new THREE.Vector3()));
         this.kerfBoxMesh.quaternion.copy(this.guidebarBox.getWorldQuaternion(new THREE.Quaternion()));
         
         // Account for the chainsaw mesh's 0.2 scale to get the true world size of the guidebar
         const worldScale = this.guidebarBox.getWorldScale(new THREE.Vector3());
         const actualWorldSize = GUIDEBAR_SIZE.clone().multiply(worldScale);
         
         this.kerfBoxMesh.scale.copy(actualWorldSize);
         this.kerfBoxMesh.visible = this.showKerfBoxDebug;
         
         // Keep cutStartPos/Quat updated for physics if needed
         this.cutStartPos = this.kerfBoxMesh.position.clone();
         this.cutStartQuat = this.kerfBoxMesh.quaternion.clone();
         
         console.log(`[ToolSystem] setThrottle(true) - KERF BOX CAPTURED:
           Pos: (${this.kerfBoxMesh.position.x.toFixed(3)}, ${this.kerfBoxMesh.position.y.toFixed(3)}, ${this.kerfBoxMesh.position.z.toFixed(3)})
           Scale: (${this.kerfBoxMesh.scale.x.toFixed(3)}, ${this.kerfBoxMesh.scale.y.toFixed(3)}, ${this.kerfBoxMesh.scale.z.toFixed(3)})
           Visible: ${this.kerfBoxMesh.visible} (debug flag: ${this.showKerfBoxDebug})
         `);
      }
    } else {
      this.isCutting = false;
      this.cutStartPos = null;
      this.cutStartQuat = null;
    }
  }

  public resetPosition() {
    if (this.chainsawMesh) {
      this.chainsawMesh.position.set(0, 5, 2);
      this.chainsawMesh.rotation.set(0, 0, 0);
      this.chainsawMesh.updateMatrixWorld(true);
    }
  }

  public resetToScenario(chainsawDef?: any) {
    this.setThrottle(false);
    this.isCutting = false;
    this.cutStartPos = null;
    this.cutStartQuat = null;

    if (this.chainsawMesh) {
      if (chainsawDef && chainsawDef.position) {
        this.chainsawMesh.position.set(
          chainsawDef.position.x,
          chainsawDef.position.y,
          chainsawDef.position.z
        );
      } else {
        this.chainsawMesh.position.set(0, 5, 2);
      }

      if (chainsawDef && chainsawDef.rotation) {
        const rx = (chainsawDef.rotation.x || 0) * Math.PI / 180;
        const ry = (chainsawDef.rotation.y || 0) * Math.PI / 180;
        const rz = (chainsawDef.rotation.z || 0) * Math.PI / 180;
        this.chainsawMesh.rotation.set(rx, ry, rz);
      } else {
        this.chainsawMesh.rotation.set(0, 0, 0);
      }
      this.chainsawMesh.updateMatrixWorld(true);

      if (this.guidebarBox && this.kerfBoxMesh) {
        this.kerfBoxMesh.position.copy(this.guidebarBox.getWorldPosition(new THREE.Vector3()));
        this.kerfBoxMesh.quaternion.copy(this.guidebarBox.getWorldQuaternion(new THREE.Quaternion()));
        const worldScale = this.guidebarBox.getWorldScale(new THREE.Vector3());
        this.kerfBoxMesh.scale.copy(GUIDEBAR_SIZE.clone().multiply(worldScale));
      }
    }
  }

  public quickFlip() {
    if (this.chainsawMesh) {
      // 180 degrees around Y axis
      this.chainsawMesh.rotateY(Math.PI);
      this.chainsawMesh.updateMatrixWorld(true);
    }
  }

  private getClosestLogInfo(): { center: THREE.Vector3, max: THREE.Vector3, min: THREE.Vector3, boundingBox: THREE.Box3, entity: any } | null {
    if (!this.chainsawMesh || !this.guidebarBox) return null;

    let closestDist = Infinity;
    let closestInfo = null;

    const sawPos = new THREE.Vector3();
    this.guidebarBox.getWorldPosition(sawPos);
    const guidebarBBox = new THREE.Box3().setFromObject(this.guidebarBox);

    for (const entity of this.entities.getAll()) {
      if (entity.type === 'log' && entity.mesh instanceof THREE.Mesh) {
        const geo = entity.mesh.geometry as THREE.BufferGeometry;
        geo.computeBoundingBox();
        if (!geo.boundingBox) continue;

        const center = new THREE.Vector3();
        geo.boundingBox.getCenter(center);

        // Get the world-space bounding box of the log
        const bbox = new THREE.Box3().setFromObject(entity.mesh);

        // Check bounding box intersection with guidebar box
        const intersects = bbox.intersectsBox(guidebarBBox);
        const dist = bbox.distanceToPoint(sawPos);

        // Prioritize logs that actually intersect with the guidebar box (score offset of 0 vs 1000)
        const score = intersects ? (dist * 0.001 + center.distanceTo(sawPos) * 0.0001) : (dist + 1000 + center.distanceTo(sawPos) * 0.0001);

        if (score < closestDist) {
          closestDist = score;
          closestInfo = {
            center,
            max: bbox.max,
            min: bbox.min,
            boundingBox: bbox,
            entity
          };
        }
      }
    }

    return closestInfo;
  }

  public snapTop() {
    if (this.chainsawMesh) {
      const log = this.getClosestLogInfo();
      if (log) {
        // Snap to the center XZ of the log, and slightly above its maximum Y
        this.chainsawMesh.position.set(log.center.x, log.max.y + 0.6, log.center.z);
        // Ensure it remains upright
        this.chainsawMesh.rotation.z = 0;
        this.chainsawMesh.rotation.x = 0;
        this.chainsawMesh.updateMatrixWorld(true);
      }
    }
  }

  public snapBottom() {
    if (this.chainsawMesh) {
      const log = this.getClosestLogInfo();
      if (log) {
        // Snap to the center XZ of the log, and slightly below its minimum Y
        this.chainsawMesh.position.set(log.center.x, log.min.y - 0.6, log.center.z);
        // Ensure it remains upright
        this.chainsawMesh.rotation.z = 0;
        this.chainsawMesh.rotation.x = 0;
        this.chainsawMesh.updateMatrixWorld(true);
      }
    }
  }

  public snapToCut(intersection: THREE.Intersection) {
    if (!this.chainsawMesh) return;
    
    // Find log entity
    const logEntity = this.entities.getAll().find(e => e.mesh === intersection.object);
    if (!logEntity || logEntity.type !== 'log') return;
    
    const geo = (logEntity.mesh as THREE.Mesh).geometry;
    geo.computeBoundingBox();
    if (!geo.boundingBox) return;
    
    const center = new THREE.Vector3();
    geo.boundingBox.getCenter(center);
    
    // Determine top or bottom cut
    const isTopCut = intersection.point.y > center.y;
    const targetY = isTopCut ? geo.boundingBox.max.y + 0.1 : geo.boundingBox.min.y - 0.1;
    
    // Target position for the center of the guidebar
    const targetPos = new THREE.Vector3(intersection.point.x, targetY, intersection.point.z);
    
    // Determine longitudinal axis to set perpendicular orientation
    let longitudinalAxis = new THREE.Vector3(1, 0, 0); // Default X-axis
    if (logEntity.segments && logEntity.segments.length >= 2 && logEntity.vertexMap) {
      const firstRing = logEntity.segments[0];
      const lastRing = logEntity.segments[logEntity.segments.length - 1];
      const posAttr = geo.getAttribute('position');
      const vIdxStart = logEntity.vertexMap[firstRing.pithIdx]?.[0];
      const vIdxEnd = logEntity.vertexMap[lastRing.pithIdx]?.[0];
      if (vIdxStart !== undefined && vIdxEnd !== undefined) {
        const startPos = new THREE.Vector3().fromBufferAttribute(posAttr, vIdxStart);
        const endPos = new THREE.Vector3().fromBufferAttribute(posAttr, vIdxEnd);
        longitudinalAxis.subVectors(endPos, startPos).normalize();
      }
    }
    
    const logAxisXZ = longitudinalAxis.clone().setY(0).normalize();
    if (logAxisXZ.lengthSq() < 0.001) logAxisXZ.set(1, 0, 0); // Fallback
 
    // Align local X (blade normal) to longitudinal axis.
    // This keeps the chainsaw perfectly upright and perpendicular to the log.
    this.chainsawMesh.rotation.set(0, 0, 0);
    this.chainsawMesh.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), logAxisXZ);
    
    // Shift position so guidebar center is at targetPos
    this.chainsawMesh.position.set(0, 0, 0); // temporarily set to origin to measure rotated offset
    this.chainsawMesh.updateMatrixWorld(true);
    
    if (this.guidebarBox) {
      const guidebarWorldPos = new THREE.Vector3();
      this.guidebarBox.getWorldPosition(guidebarWorldPos);
      
      // offset is guidebarWorldPos relative to chainsaw origin
      this.chainsawMesh.position.copy(targetPos).sub(guidebarWorldPos);
    } else {
      this.chainsawMesh.position.copy(targetPos);
    }
    this.chainsawMesh.updateMatrixWorld(true);
  }

  public syncWedges(wedges?: WedgeState[]) {
    if (!wedges) return;

    const activeIds = new Set<string>();

    for (const w of wedges) {
      const entityId = `wedge-${w.id}`;
      activeIds.add(entityId);

      let entity = this.entities.get(entityId);
      if (!entity) {
        const mesh = createWedgeMesh(entityId);
        entity = {
          id: entityId,
          type: 'wedge',
          mesh
        };
        this.entities.add(entity);
      }

      if (entity.mesh) {
        entity.mesh.position.set(w.position[0], w.position[1], w.position[2]);
        entity.mesh.quaternion.set(w.quaternion[0], w.quaternion[1], w.quaternion[2], w.quaternion[3]);
        entity.mesh.visible = true;
        entity.mesh.updateMatrixWorld(true);
      }
    }

    // Remove wedges that are no longer active in the physics worker
    for (const e of this.entities.getAll()) {
      if (e.type === 'wedge' && !activeIds.has(e.id)) {
        this.entities.remove(e.id);
      }
    }
  }

  public findClosestKerf(intersectionPoint: THREE.Vector3): { logEntity: any, kerfIndex: number, radialPosition: 'top' | 'bottom' } | null {
    let closestDist = Infinity;
    let result: { logEntity: any, kerfIndex: number, radialPosition: 'top' | 'bottom' } | null = null;

    for (const entity of this.entities.getAll()) {
      if (entity.type !== 'log' || !entity.dynamicIndices || !entity.dynamicIndices.kerfs) continue;

      for (let k = 0; k < entity.dynamicIndices.kerfs.length; k++) {
        const kerf = entity.dynamicIndices.kerfs[k];
        const globalKerfIdx = kerf.globalKerfIndex;

        // Estimate kerf center from endcap nodes or bridge vertices
        const mesh = entity.mesh as THREE.Mesh;
        const posAttr = mesh.geometry.getAttribute('position');
        if (!posAttr) continue;

        // Take average of first endcap slice vertices to get rough cut plane center
        if (kerf.endcapIndices && kerf.endcapIndices.length > 0) {
          const slice0 = kerf.endcapIndices[0];
          if (slice0 && slice0.length >= 3) {
            const v0 = new THREE.Vector3().fromBufferAttribute(posAttr, slice0[0]);
            const dist = v0.distanceTo(intersectionPoint);
            if (dist < closestDist) {
              closestDist = dist;
              // Determine if click is top or bottom relative to log center
              const geo = mesh.geometry;
              geo.computeBoundingBox();
              const center = new THREE.Vector3();
              if (geo.boundingBox) geo.boundingBox.getCenter(center);
              const isTop = intersectionPoint.y >= center.y;

              result = {
                logEntity: entity,
                kerfIndex: globalKerfIdx,
                radialPosition: isTop ? 'top' : 'bottom'
              };
            }
          }
        }
      }
    }

    // Fallback: if dynamicIndices not available, default to first kerf if distance is reasonable
    if (!result) {
      for (const entity of this.entities.getAll()) {
        if (entity.type === 'log') {
          const mesh = entity.mesh as THREE.Mesh;
          const geo = mesh.geometry;
          geo.computeBoundingBox();
          const center = new THREE.Vector3();
          if (geo.boundingBox) geo.boundingBox.getCenter(center);
          const isTop = intersectionPoint.y >= center.y;
          return {
            logEntity: entity,
            kerfIndex: 0,
            radialPosition: isTop ? 'top' : 'bottom'
          };
        }
      }
    }

    return result;
  }
}
